import React, { useState, useEffect, useRef } from 'react'
import { Formik } from 'formik'
import { Form } from 'react-bootstrap'
import PropTypes from 'prop-types'
import ExperienceDatePicker from '../form/ExperienceDatePicker'
import ExperienceSelect from '../form/ExperienceSelect'
import RsvpTimePicker from '../form/RsvpTimePicker'
import QuestHOC from './QuestHOC'
import {
  autosaveAfterFieldChange,
  queueAutosaveOnBlur,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'
import {
  buildUtcRsvpPayload,
  parseUtcRsvpForForm,
  validateRsvpForm,
  getRsvpFieldErrors,
} from '../../utils/rsvpDateTime'
import { getCurrentDate } from '../../utils/date'
import './RsvpInfoForm.scss'

const RSVP_TYPES = {
  UPCOMING: 'upcoming',
  RECORDED: 'recorded',
}

const EMPTY_FIELD_ERRORS = {
  rsvpDate: false,
  rsvpTime: false,
  rsvpLink: false,
  rsvpRecordingLink: false,
}

const inferRsvpType = (rsvp = {}) => {
  const hasUpcoming = Boolean(rsvp.date || rsvp.time || rsvp.link)
  const hasRecorded = Boolean(rsvp.recordingLink?.trim())
  if (hasRecorded && !hasUpcoming) {
    return RSVP_TYPES.RECORDED
  }
  return RSVP_TYPES.UPCOMING
}

const getInitialValues = (questDetail) => {
  const rsvp = questDetail?.learnMore?.rsvp || {}
  const { date, time } = parseUtcRsvpForForm(rsvp.date, rsvp.time)
  return {
    rsvpDate: date,
    rsvpTime: time,
    rsvpLink: rsvp.link || '',
    rsvpRecordingLink: rsvp.recordingLink || '',
  }
}

const RsvpInfoForm = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
}) => {
  const busy = !!isFormSubmitting
  const [initialValues, setInitialValues] = useState(() =>
    getInitialValues(questDetail),
  )
  const valuesRef = useRef(initialValues)
  const [rsvpType, setRsvpType] = useState(() =>
    inferRsvpType(questDetail?.learnMore?.rsvp),
  )
  const [fieldErrors, setFieldErrors] = useState(EMPTY_FIELD_ERRORS)

  useEffect(() => {
    setRsvpType(inferRsvpType(questDetail?.learnMore?.rsvp))
    const next = getInitialValues(questDetail)
    setInitialValues(next)
    valuesRef.current = next
  }, [questDetail])

  const buildPayload = (values) => {
    const savedRsvp = questDetail?.learnMore?.rsvp || {}
    const link = values.rsvpLink?.trim() || ''
    const recordingLink = values.rsvpRecordingLink?.trim() || ''

    if (rsvpType === RSVP_TYPES.RECORDED) {
      return {
        learnMore: {
          ...(questDetail?.learnMore || {}),
          rsvp: {
            date: savedRsvp.date || '',
            time: savedRsvp.time || '',
            link: savedRsvp.link || '',
            recordingLink,
          },
        },
      }
    }

    const validation = validateRsvpForm(
      values.rsvpDate,
      values.rsvpTime,
      values.rsvpLink,
    )
    const { date, time } = validation.isComplete
      ? buildUtcRsvpPayload(values.rsvpDate, values.rsvpTime)
      : { date: '', time: '' }

    return {
      learnMore: {
        ...(questDetail?.learnMore || {}),
        rsvp: {
          date,
          time,
          link,
          recordingLink: savedRsvp.recordingLink || '',
        },
      },
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={(values) => {
        if (rsvpType === RSVP_TYPES.RECORDED) {
          setFieldErrors(EMPTY_FIELD_ERRORS)
          return {}
        }

        const validation = validateRsvpForm(
          values.rsvpDate,
          values.rsvpTime,
          values.rsvpLink,
        )

        if (validation.isValid) {
          setFieldErrors(EMPTY_FIELD_ERRORS)
          return {}
        }

        const errs = getRsvpFieldErrors(
          values.rsvpDate,
          values.rsvpTime,
          values.rsvpLink,
        )
        setFieldErrors(errs)

        // All three are required together — message each missing field.
        const formikErrors = {}
        if (errs.rsvpDate) formikErrors.rsvpDate = 'Date is required'
        if (errs.rsvpTime) formikErrors.rsvpTime = 'Pacific time is required'
        if (errs.rsvpLink) formikErrors.rsvpLink = 'RSVP link is required'
        return formikErrors
      }}
      onSubmit={async (values, { resetForm }) => {
        await runQueuedFormikSave({
          values,
          valuesRef,
          buildPayload,
          resetForm,
          setInitialValues,
          save: (payload) => updateQuestDetails(payload, { silent: true }),
        })
      }}>
      {(formik) => {
        const { handleSubmit, values, setFieldValue, dirty, errors } = formik
        valuesRef.current = formik.values
        const fieldBlur = () => queueAutosaveOnBlur(formik)
        const savedRsvpDate = questDetail?.learnMore?.rsvp?.date
        const savedRsvpTime = questDetail?.learnMore?.rsvp?.time

        return (
          <form
            onSubmit={handleSubmit}
            className='rsvp-info-form'
            aria-busy={busy}>
            <FormikDirtyReporter id='content-rsvp' dirty={dirty} />
            <div className='experience-f-row'>
              <ExperienceSelect
                id='rsvpType'
                label='Type'
                value={rsvpType}
                onChange={(event) => setRsvpType(event.target.value)}
                options={[
                  {
                    value: RSVP_TYPES.UPCOMING,
                    label: 'Upcoming session',
                  },
                  {
                    value: RSVP_TYPES.RECORDED,
                    label: 'Recorded session',
                  },
                ]}
              />
            </div>

            <div
              className='experience-rsvp-block'
              data-rsvp-block='upcoming'
              hidden={rsvpType !== RSVP_TYPES.UPCOMING}>
              <div className='experience-f-row'>
                <div className='experience-f'>
                  <ExperienceDatePicker
                    name='rsvpDate'
                    label='Date'
                    showErrorMessage
                    isInvalid={fieldErrors.rsvpDate}
                    errorMessage={
                      fieldErrors.rsvpDate
                        ? errors.rsvpDate || 'Date is required'
                        : undefined
                    }
                    className='experience-f-control'
                    labelProps={{ className: 'experience-f-label' }}
                    displayFlex={false}
                    minimumDate={getCurrentDate()}
                    onValueChange={() => autosaveAfterFieldChange(formik)}
                  />
                </div>
                <div className='experience-f'>
                  <label className='experience-f-label'>Pacific time</label>
                  <RsvpTimePicker
                    value={values.rsvpTime}
                    utcDate={savedRsvpDate}
                    utcTime={savedRsvpTime}
                    isInvalid={fieldErrors.rsvpTime}
                    onChange={(nextTime) => {
                      autosaveAfterFieldChange(
                        formik,
                        setFieldValue('rsvpTime', nextTime, false),
                      )
                    }}
                    onBlur={fieldBlur}
                  />
                  {fieldErrors.rsvpTime ? (
                    <p className='experience-f-error mt-1 mb-0' role='alert'>
                      {errors.rsvpTime || 'Pacific time is required'}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className='experience-f-row'>
                <div className='experience-f'>
                  <Form.Group className='mb-0'>
                    <Form.Label className='experience-f-label'>
                      RSVP link
                    </Form.Label>
                    <Form.Control
                      type='text'
                      inputMode='url'
                      autoComplete='url'
                      className={`experience-f-control${
                        fieldErrors.rsvpLink ? ' is-invalid' : ''
                      }`}
                      placeholder='https://'
                      value={values.rsvpLink}
                      onChange={(event) =>
                        setFieldValue('rsvpLink', event.target.value)
                      }
                      onBlur={fieldBlur}
                    />
                    {fieldErrors.rsvpLink ? (
                      <p className='experience-f-error mt-1 mb-0' role='alert'>
                        {errors.rsvpLink || 'RSVP link is required'}
                      </p>
                    ) : null}
                  </Form.Group>
                </div>
              </div>
            </div>

            <div
              className='experience-rsvp-block'
              data-rsvp-block='recorded'
              hidden={rsvpType !== RSVP_TYPES.RECORDED}>
              <div className='experience-f-row'>
                <div className='experience-f'>
                  <Form.Group className='mb-0'>
                    <Form.Label className='experience-f-label'>URL</Form.Label>
                    <Form.Control
                      type='url'
                      className='experience-f-control'
                      placeholder='https://'
                      value={values.rsvpRecordingLink}
                      onChange={(event) =>
                        setFieldValue('rsvpRecordingLink', event.target.value)
                      }
                      onBlur={fieldBlur}
                    />
                  </Form.Group>
                </div>
              </div>
            </div>

            <button type='submit' hidden aria-hidden />
          </form>
        )
      }}
    </Formik>
  )
}

RsvpInfoForm.propTypes = {
  questDetail: PropTypes.object,
  updateQuestDetails: PropTypes.func,
  isFormSubmitting: PropTypes.bool,
}

export default QuestHOC(RsvpInfoForm)
